import React, { useMemo, useState } from "react";
import { Alert, Box, Button, MenuItem, Stack, TextField, Typography } from "@mui/material";
import SmsRoundedIcon from "@mui/icons-material/SmsRounded";
import SendIcon from "@mui/icons-material/Send";
import CallMadeIcon from "@mui/icons-material/CallMade";
import CallReceivedIcon from "@mui/icons-material/CallReceived";
import {
  useGetSmsMessagesQuery, useSendTestSmsMutation, useGetNumbersQuery,
} from "../../../Api/platformApi";
import {
  AppPage, PageHeader, DataTable, StatusBadge, FilterBar, SearchBox,
  ActionButton, SlidePanel, type DataTableColumn,
} from "../../../Components/ui";
import { NAVY } from "../../../theme/tokens";

const SmsLogs: React.FC = () => {
  const [filters, setFilters] = useState<{ direction?: string; status?: string }>({});
  const { data, isLoading, refetch } = useGetSmsMessagesQuery(filters);
  const { data: numbersData } = useGetNumbersQuery();
  const [sendTest, { isLoading: sending }] = useSendTestSmsMutation();

  const [open, setOpen] = useState(false);
  const [form, setForm] = useState<any>({ number_id: "", to: "", message: "INTALK test message ✅" });
  const [result, setResult] = useState<{ ok: boolean; msg: string } | null>(null);
  const [search, setSearch] = useState("");

  const rows = data?.data ?? [];
  const numbers = numbersData?.data ?? [];

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((m: any) =>
      [m.from_number, m.to_number, m.body, m.provider, m.status]
        .filter(Boolean).some((v: string) => String(v).toLowerCase().includes(q)));
  }, [rows, search]);

  const runTest = async () => {
    setResult(null);
    try {
      const res: any = await sendTest({
        number_id: form.number_id || undefined, to: form.to, message: form.message,
      }).unwrap();
      setResult({ ok: !!res.status, msg: res.message || "Sent" });
      refetch();
    } catch (e: any) {
      setResult({ ok: false, msg: e?.data?.message || "Failed to send" });
    }
  };

  const columns: DataTableColumn<any>[] = [
    {
      id: "created_at", label: "Date",
      render: (m) => (
        <Typography fontSize={13} color="text.secondary">
          {new Date(m.created_at).toLocaleString()}
        </Typography>
      ),
    },
    {
      id: "direction", label: "Direction",
      render: (m) => (
        <Stack direction="row" alignItems="center" spacing={0.75}>
          {m.direction === "inbound"
            ? <CallReceivedIcon sx={{ fontSize: 15, color: NAVY, opacity: 0.7 }} />
            : <CallMadeIcon sx={{ fontSize: 15, color: NAVY, opacity: 0.5 }} />}
          <Typography fontSize={13} fontWeight={600} color={NAVY} sx={{ textTransform: "capitalize" }}>
            {m.direction}
          </Typography>
        </Stack>
      ),
    },
    { id: "from_number", label: "From", render: (m) => m.from_number || "—" },
    { id: "to_number", label: "To", render: (m) => m.to_number || "—" },
    {
      id: "body", label: "Message",
      render: (m) => (
        <Box sx={{ maxWidth: 320, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
          <Typography fontSize={13}>{m.body || "—"}</Typography>
        </Box>
      ),
    },
    {
      id: "status", label: "Status",
      render: (m) => <StatusBadge status={m.status} />,
    },
    {
      id: "provider", label: "Provider",
      render: (m) => (
        <Typography fontSize={13} color="text.secondary">{m.provider || "—"}</Typography>
      ),
    },
  ];

  return (
    <AppPage
      disablePaper
      header={
        <PageHeader
          icon={<SmsRoundedIcon />}
          title="SMS Logs"
          subtitle="Inbound and outbound message history"
          actions={
            <ActionButton
              startIcon={<SendIcon />}
              onClick={() => { setResult(null); setOpen(true); }}
            >
              Send Test SMS
            </ActionButton>
          }
        />
      }
    >
      <FilterBar
        right={
          <Typography fontSize={13} color="text.secondary">
            {filtered.length} message{filtered.length !== 1 ? "s" : ""}
          </Typography>
        }
      >
        <SearchBox value={search} onChange={setSearch} placeholder="Search messages…" />
        <TextField
          select size="small" label="Direction" sx={{ width: 160 }}
          value={filters.direction ?? ""}
          onChange={(e) => setFilters({ ...filters, direction: e.target.value || undefined })}
        >
          <MenuItem value="">All</MenuItem>
          <MenuItem value="outbound">Outbound</MenuItem>
          <MenuItem value="inbound">Inbound</MenuItem>
        </TextField>
        <TextField
          select size="small" label="Status" sx={{ width: 160 }}
          value={filters.status ?? ""}
          onChange={(e) => setFilters({ ...filters, status: e.target.value || undefined })}
        >
          <MenuItem value="">All</MenuItem>
          {["queued", "sending", "sent", "delivered", "failed", "received"].map((s) => (
            <MenuItem key={s} value={s}>{s}</MenuItem>
          ))}
        </TextField>
      </FilterBar>

      <DataTable
        columns={columns}
        rows={filtered}
        loading={isLoading}
        empty={{
          icon: <SmsRoundedIcon />,
          title: "No messages yet",
          message: "Messages will appear here once your numbers send or receive SMS.",
        }}
      />

      <SlidePanel
        open={open}
        onClose={() => setOpen(false)}
        title="Send Test SMS"
        subtitle="Verify your SMS configuration"
        icon={<SendIcon />}
        footer={
          <>
            <Button variant="outlined" onClick={() => setOpen(false)} sx={{ borderRadius: "10px" }}>
              Close
            </Button>
            <ActionButton onClick={runTest} disabled={!form.to || sending}>
              {sending ? "Sending…" : "Send"}
            </ActionButton>
          </>
        }
      >
        <Stack spacing={2}>
          {result && <Alert severity={result.ok ? "success" : "error"}>{result.msg}</Alert>}
          <TextField
            select label="From Number" value={form.number_id}
            onChange={(e) => setForm({ ...form, number_id: e.target.value })} fullWidth
          >
            <MenuItem value="">Auto (first SMS number)</MenuItem>
            {numbers.filter((n: any) => n.sms_enabled).map((n: any) => (
              <MenuItem key={n.id} value={n.id}>{n.number} ({n.sms_provider})</MenuItem>
            ))}
          </TextField>
          <TextField
            label="To (E.164, e.g. +14165551234)" value={form.to}
            onChange={(e) => setForm({ ...form, to: e.target.value })} fullWidth required
          />
          <TextField
            label="Message" value={form.message} multiline minRows={2}
            onChange={(e) => setForm({ ...form, message: e.target.value })} fullWidth
          />
        </Stack>
      </SlidePanel>
    </AppPage>
  );
};

export default SmsLogs;
